// useCall — the one way a screen reaches the transport.
//
// It exists so that every screen handles the same four states, and so that NO screen can render a
// success path while a call is still in flight or has failed. `pending` is not `failed` and neither is
// `ok`: the three are different in the type, for the same reason the contract makes "never ran"
// different from "ran and found nothing".
//
// The generated client never throws — a network drop, a token provider that threw, an unreadable body
// and a typed refusal all arrive as `{ ok: false }` values — so there is no try/catch here and no
// error boundary standing in for one.

import { useEffect, useRef, useState } from "react";
import type { V2Failure, V2Result } from "@contract";

export type CallState<T> =
  | { status: "pending" }
  | { status: "ok"; response: T }
  | { status: "failed"; failure: V2Failure };

/**
 * Runs `call` whenever `deps` change and returns its state.
 *
 * A LATE ANSWER NEVER WINS. Each run takes a sequence number and a result is dropped unless it is the
 * newest, so a slow first call cannot overwrite a fast second one — which on this client would mean
 * rendering a decision about a property the owner has already moved on from.
 */
export function useCall<T>(call: () => Promise<V2Result<T>>, deps: readonly unknown[]): CallState<T> {
  const [state, setState] = useState<CallState<T>>({ status: "pending" });
  const seq = useRef(0);
  const callRef = useRef(call);
  callRef.current = call;

  useEffect(() => {
    const mine = ++seq.current;
    let alive = true;
    setState({ status: "pending" });
    void callRef.current().then((result) => {
      if (!alive || mine !== seq.current) return;
      setState(result.ok ? { status: "ok", response: result.response } : { status: "failed", failure: result });
    });
    return () => {
      alive = false;
    };
    // The dependency list is the CALLER's: a screen says what its call depends on, and `callRef` keeps
    // the closure fresh without making the identity of `call` a dependency.
  }, deps);

  return state;
}

/** For calls the owner starts: a button press, not a render. */
export function useAction<Args extends unknown[], T>(
  action: (...args: Args) => Promise<V2Result<T>>,
): {
  state: CallState<T> | { status: "idle" };
  run: (...args: Args) => Promise<V2Result<T>>;
} {
  const [state, setState] = useState<CallState<T> | { status: "idle" }>({ status: "idle" });
  const run = async (...args: Args) => {
    setState({ status: "pending" });
    const result = await action(...args);
    setState(result.ok ? { status: "ok", response: result.response } : { status: "failed", failure: result });
    return result;
  };
  return { state, run };
}
